import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Paper,
  Tooltip
} from '@mui/material';
import type { AddItemDto } from '@azoupvendas/shared';
import { usePedidoStore } from '../stores/pedido.store';
import { usePricingService } from '../hooks/usePricingService';
import { useAuth } from '@/hooks/useAuth';
import type { ProdutoPricing } from '../services/pricing.service';
import { varianteToPricing, precosPorTamanho } from '../services/grade-pricing';
import type { GradeVariante } from '../services/grade-pricing';

// AIDEV-NOTE: Re-export para compatibilidade — CatalogoProduto importa o tipo daqui
export type { GradeVariante };

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

interface GradeProdutoProps {
  produto: {
    CODIGO: number;
    REFERENCIA: string;
    DESCRICAO: string;
    preco_custo?: number | null;
    codbarras?: string | null;
  } & ProdutoPricing;
  variantes: GradeVariante[];
  onClose: () => void;
}

// AIDEV-NOTE: Tabela MUI de grade onde linhas = cores, colunas = tamanhos.
// Cada celula tem input numerico de quantidade + saldo abaixo.
// A grade abre PREENCHIDA com o que ja esta no carrinho e o botao SUBSTITUI as quantidades
// (mesmo comportamento do card de produto simples) — digitar de novo nao soma/duplica.

export function GradeProduto({ produto, variantes, onClose }: GradeProdutoProps) {
  const addItem = usePedidoStore((s) => s.addItem);
  const updateItem = usePedidoStore((s) => s.updateItem);
  const removeItem = usePedidoStore((s) => s.removeItem);
  const { calcularPreco } = usePricingService();
  const { user } = useAuth();
  const exibirEstoque = user?.exibir_estoque_vendedor ?? true;

  // AIDEV-NOTE: Ordena cores e tamanhos pelo codigo numerico do ERP (codcor/codtamanho)
  // para garantir exibicao P→M→G→GG em vez da ordem de insercao no banco.
  const cores = useMemo(() => {
    const map = new Map<string, number>();
    for (const v of variantes) {
      if (!map.has(v.cor) || v.codcor < map.get(v.cor)!) map.set(v.cor, v.codcor);
    }
    return [...map.entries()].sort((a, b) => a[1] - b[1]).map(([cor]) => cor);
  }, [variantes]);

  const tamanhos = useMemo(() => {
    const map = new Map<string, number>();
    for (const v of variantes) {
      if (!map.has(v.tamanho) || v.codtamanho < map.get(v.tamanho)!)
        map.set(v.tamanho, v.codtamanho);
    }
    return [...map.entries()].sort((a, b) => a[1] - b[1]).map(([tam]) => tam);
  }, [variantes]);

  // AIDEV-NOTE: preco por coluna de tamanho (qtd=1); se variar por cor no tamanho, mostra min–max
  const precoHeader = useMemo(() => {
    const lista = precosPorTamanho(
      variantes,
      (v) => calcularPreco(varianteToPricing(produto, v), 1).preco_unitario
    );
    const map: Record<string, string> = {};
    for (const t of lista) {
      map[t.tamanho] =
        t.min === t.max
          ? formatCurrency(t.min)
          : `${formatCurrency(t.min)}–${formatCurrency(t.max)}`;
    }
    return map;
  }, [variantes, calcularPreco, produto]);

  const getKey = (cor: string, tamanho: string) => `${cor}|${tamanho}`;

  const getVariante = useCallback(
    (cor: string, tamanho: string) => variantes.find((v) => v.cor === cor && v.tamanho === tamanho),
    [variantes]
  );

  // AIDEV-NOTE: celulas validas (cor|tamanho) + indice reverso "codcor|codtamanho" -> celula,
  // usados para casar os itens ja no carrinho com as celulas desta grade.
  const celulas = useMemo(() => {
    const validas = new Set<string>();
    const porCodigo = new Map<string, string>();
    for (const v of variantes) {
      const key = `${v.cor}|${v.tamanho}`;
      validas.add(key);
      if (v.codcor > 0 && v.codtamanho > 0) porCodigo.set(`${v.codcor}|${v.codtamanho}`, key);
    }
    return { validas, porCodigo };
  }, [variantes]);

  // AIDEV-NOTE: casa item do carrinho -> celula. Prioriza codcor/codtamanho (codigos do ERP);
  // fallback por nome cobre itens de rascunho antigo gravados sem esses codigos.
  const celulaDoItem = useCallback(
    (item: AddItemDto): string | null => {
      if (item.produto_codigo !== produto.CODIGO) return null;
      const codcor = item.codcor ?? 0;
      const codtamanho = item.codtamanho ?? 0;
      if (codcor > 0 && codtamanho > 0) {
        const key = celulas.porCodigo.get(`${codcor}|${codtamanho}`);
        if (key) return key;
      }
      const porNome = `${item.cor ?? ''}|${item.tamanho ?? ''}`;
      return celulas.validas.has(porNome) ? porNome : null;
    },
    [celulas, produto.CODIGO]
  );

  // AIDEV-NOTE: agrupa as linhas do carrinho por celula. Pode haver mais de uma linha na mesma
  // celula quando o preco foi editado a mao entre duas adicoes — somamos para exibir e
  // consolidamos ao salvar.
  const agruparCarrinho = useCallback(
    (itens: AddItemDto[]) => {
      const map = new Map<string, { indices: number[]; quantidade: number }>();
      itens.forEach((item, idx) => {
        const key = celulaDoItem(item);
        if (!key) return;
        const atual = map.get(key);
        if (atual) {
          atual.indices.push(idx);
          atual.quantidade += item.quantidade;
        } else {
          map.set(key, { indices: [idx], quantidade: item.quantidade });
        }
      });
      return map;
    },
    [celulaDoItem]
  );

  // AIDEV-NOTE: snapshot do carrinho no momento em que a grade abriu — so para pre-preencher os
  // campos e decidir o rotulo do botao. O salvar le o estado atual do store (indices frescos).
  const [carrinhoInicial] = useState(() => agruparCarrinho(usePedidoStore.getState().itens));

  // Estado: Record<"cor|tamanho", quantidade>
  const [quantidades, setQuantidades] = useState<Record<string, number>>(() => {
    const inicial: Record<string, number> = {};
    for (const [key, grupo] of carrinhoInicial) {
      if (grupo.quantidade > 0) inicial[key] = grupo.quantidade;
    }
    return inicial;
  });

  const jaNoCarrinho = carrinhoInicial.size > 0;

  const handleQuantidadeChange = (cor: string, tamanho: string, value: string) => {
    const key = getKey(cor, tamanho);
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) {
      setQuantidades((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setQuantidades((prev) => ({ ...prev, [key]: num }));
    }
  };

  const totalItens = useMemo(
    () => Object.values(quantidades).reduce((sum, q) => sum + (q || 0), 0),
    [quantidades]
  );

  const handleAdicionar = () => {
    // AIDEV-NOTE: le o carrinho ATUAL (nao o snapshot da abertura) para que os indices passados a
    // updateItem/removeItem sejam validos mesmo se o carrinho mudou com o dialog aberto.
    const grupos = agruparCarrinho(usePedidoStore.getState().itens);
    const remover: number[] = [];
    const novos: AddItemDto[] = [];

    for (const cor of cores) {
      for (const tamanho of tamanhos) {
        const key = getKey(cor, tamanho);
        const qtd = quantidades[key] ?? 0;
        const grupo = grupos.get(key);

        if (grupo) {
          const [principal, ...extras] = grupo.indices;
          // AIDEV-NOTE: consolidacao — linhas duplicadas da mesma celula viram uma so; o preco
          // preservado e o da primeira linha.
          remover.push(...extras);
          if (qtd > 0) {
            // AIDEV-NOTE: valor_unitario NAO e recalculado — preserva preco editado a mao no carrinho
            if (qtd !== grupo.quantidade || extras.length > 0) {
              updateItem(principal!, { quantidade: qtd });
            }
          } else {
            // celula zerada = item removido do pedido
            remover.push(principal!);
          }
          continue;
        }

        if (qtd <= 0) continue;

        const variante = getVariante(cor, tamanho);
        // AIDEV-NOTE: preco DA VARIANTE — tamanhos podem ter precos diferentes (fix P-G/G1-G3)
        const preco = calcularPreco(variante ? varianteToPricing(produto, variante) : produto, qtd);
        novos.push({
          produto_codigo: produto.CODIGO,
          referencia: produto.REFERENCIA,
          descricao: produto.DESCRICAO,
          cor,
          tamanho,
          quantidade: qtd,
          valor_unitario: preco.preco_unitario,
          desconto_percentual: 0,
          custo: produto.preco_custo ?? null,
          codbarras: produto.codbarras ?? null,
          codcor: variante?.codcor ?? 0,
          codtamanho: variante?.codtamanho ?? 0
        });
      }
    }

    for (const item of novos) addItem(item);
    // AIDEV-NOTE: remove por ultimo e em ordem decrescente — addItem so anexa no fim, entao os
    // indices coletados acima continuam validos.
    for (const idx of [...new Set(remover)].sort((a, b) => b - a)) removeItem(idx);

    onClose();
  };

  return (
    <Box>
      {/* AIDEV-NOTE: overflow-x:auto permite scroll horizontal no mobile quando ha muitos tamanhos */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: '55vh', overflowX: 'auto' }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {/* AIDEV-NOTE: minWidth reduzido para caber mais colunas no mobile */}
              <TableCell sx={{ fontWeight: 600, minWidth: 72, whiteSpace: 'nowrap' }}>
                Cor / Tam
              </TableCell>
              {tamanhos.map((tam) => (
                <TableCell
                  key={tam}
                  align="center"
                  sx={{ fontWeight: 600, minWidth: 52, px: 0.25, whiteSpace: 'nowrap' }}
                >
                  {tam}
                  <Typography
                    variant="caption"
                    display="block"
                    color="primary.main"
                    sx={{ fontWeight: 600 }}
                  >
                    {precoHeader[tam]}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {cores.map((cor) => (
              <TableRow key={cor}>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap', py: 0.5 }}>{cor}</TableCell>
                {tamanhos.map((tam) => {
                  const variante = getVariante(cor, tam);
                  const saldo = variante?.saldo_estoque ?? 0;
                  const key = getKey(cor, tam);
                  // AIDEV-NOTE: variante sem preco nao pode receber qtde — addItemSchema exige
                  // valor_unitario .positive(), e um item zerado derruba o finalizar E o auto-save
                  // do rascunho. Usa o preco EFETIVO (promocao/tabela entram aqui), nao preco_venda cru.
                  const precoCelula = variante
                    ? calcularPreco(varianteToPricing(produto, variante), 1).preco_unitario
                    : 0;
                  const semPreco = !(precoCelula > 0);

                  return (
                    <TableCell key={tam} align="center" sx={{ p: 0.25 }}>
                      {variante ? (
                        <Box>
                          <Tooltip
                            title={semPreco ? 'Produto sem preço cadastrado neste tamanho' : ''}
                            describeChild
                          >
                            {/* AIDEV-NOTE: span necessario — Tooltip do MUI nao dispara em elemento disabled */}
                            <span>
                              <TextField
                                type="number"
                                size="small"
                                disabled={semPreco}
                                value={quantidades[key] ?? ''}
                                onChange={(e) => handleQuantidadeChange(cor, tam, e.target.value)}
                                // AIDEV-NOTE: select() ao focar -> digitar substitui a qtd ja preenchida
                                onFocus={(e) => e.target.select()}
                                // AIDEV-NOTE: inputMode="numeric" abre teclado numerico no mobile
                                inputProps={{
                                  min: 0,
                                  inputMode: 'numeric',
                                  style: { textAlign: 'center', width: 32 }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                                  '& input': { px: 0.25, py: 0.5 }
                                }}
                              />
                            </span>
                          </Tooltip>
                          {exibirEstoque && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{
                                color: saldo === 0 ? 'error.main' : 'text.secondary',
                                mt: 0.25
                              }}
                            >
                              {saldo}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          --
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 1.5,
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total de itens: <strong>{totalItens}</strong>
          </Typography>
          {jaNoCarrinho && (
            <Typography variant="caption" color="text.secondary">
              Editando a grade já lançada — zerar um campo remove o item
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={totalItens === 0 && !jaNoCarrinho}
            onClick={handleAdicionar}
          >
            {jaNoCarrinho ? 'Salvar grade' : 'Adicionar grade'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
